var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "theme-ui/jsx-runtime";
import React from 'react';
import _CircleIcon from '@mui/icons-material/Circle';
import _CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import _SquareIcon from '@mui/icons-material/Square';
import _SquareOutlinedIcon from '@mui/icons-material/SquareOutlined';
import { COLOR, Label, TYPOGRAPHY_TYPE } from '../../..';
import { FONT_SIZE, getTypographyStyles } from '../../../theme/typography';
import { Icon } from '../Icon';
import { importedDefaultComponentShim } from '../../../utils/misc';
import { useLinesHeight } from '../ReadMore';
import { Column } from '../layout/Column';
import { useEnvironment } from '../../providers/env';
const CircleIcon = importedDefaultComponentShim(_CircleIcon);
const CircleOutlinedIcon = importedDefaultComponentShim(_CircleOutlinedIcon);
const SquareIcon = importedDefaultComponentShim(_SquareIcon);
const SquareOutlinedIcon = importedDefaultComponentShim(_SquareOutlinedIcon);
export var LIST_SIZE;
(function (LIST_SIZE) {
    LIST_SIZE["SM"] = "sm";
    LIST_SIZE["MD"] = "md";
    LIST_SIZE["LG"] = "lg";
})(LIST_SIZE || (LIST_SIZE = {}));
const marginSizeMap = {
    [LIST_SIZE.SM]: 2,
    [LIST_SIZE.MD]: 3,
    [LIST_SIZE.LG]: 4,
};
const iconSizeMap = {
    [LIST_SIZE.SM]: FONT_SIZE.XXS,
    [LIST_SIZE.MD]: FONT_SIZE.XXS,
    [LIST_SIZE.LG]: FONT_SIZE.XS,
};
const defaultIcons = [
    CircleIcon,
    CircleOutlinedIcon,
    SquareIcon,
    SquareOutlinedIcon,
];
const sizeTypographyMap = {
    [LIST_SIZE.SM]: TYPOGRAPHY_TYPE.CONDENSED_TEXT_SMALL,
    [LIST_SIZE.MD]: TYPOGRAPHY_TYPE.CONDENSED_TEXT_MEDIUM,
    [LIST_SIZE.LG]: TYPOGRAPHY_TYPE.CONDENSED_TEXT_LARGE,
};
const ListConfigContext = React.createContext({
    size: LIST_SIZE.MD,
    ordered: false,
    icon: CircleIcon,
    iconColor: COLOR.TEXT,
    iconSize: undefined,
    depth: -1,
});
const useListConfig = () => React.useContext(ListConfigContext);
export const List = ({ sx, size: sizeParam, ordered, icon, iconSize: iconSizeParam, iconColor: iconColorParam, children, }) => {
    const { depth: parentDepth, size: parentSize, iconColor: parentIconColor, iconSize: parentIconSize, } = useListConfig();
    const depth = parentDepth + 1;
    const defaultStyles = Object.assign({ listStyleType: 'none' }, getTypographyStyles());
    const defaultIcon = defaultIcons[depth] || CircleIcon;
    const size = sizeParam || parentSize || LIST_SIZE.MD;
    const iconSize = iconSizeParam || parentIconSize || iconSizeMap[size];
    const iconColor = iconColorParam || parentIconColor || COLOR.TEXT;
    return (_jsx(ListConfigContext.Provider, Object.assign({ value: {
            size,
            icon: icon || defaultIcon,
            iconColor,
            iconSize,
            ordered: ordered || false,
            depth,
        } }, { children: ordered ? (_jsx("ul", Object.assign({ sx: Object.assign(Object.assign({}, defaultStyles), sx) }, { children: children }))) : (_jsx("ol", Object.assign({ sx: Object.assign(Object.assign({}, defaultStyles), sx) }, { children: children }))) })));
};
export const ListItem = (_a) => {
    var { index, sx, icon: iconParam, iconSize: iconSizeParam, iconColor: iconColorParam, onIconClick, onClick, children, iconLabel, tabIndex: tabIndexParam } = _a, rest = __rest(_a, ["index", "sx", "icon", "iconSize", "iconColor", "onIconClick", "onClick", "children", "iconLabel", "tabIndex"]);
    const { ordered, icon: listIcon, iconColor: listIconColor, iconSize: listIconSize, size, depth, } = useListConfig();
    const { flagInDevelopment } = useEnvironment();
    if (onIconClick && !iconLabel) {
        flagInDevelopment('ListItem component with onIconClick should have an iconLabel prop for accessibility');
    }
    const icon = iconParam || listIcon;
    const iconSize = iconSizeParam || listIconSize;
    const iconColor = iconColorParam || listIconColor;
    const typography = sizeTypographyMap[size];
    const typographyStyles = getTypographyStyles(sizeTypographyMap[size]);
    const lineHeight = useLinesHeight({ typography, lines: 1 });
    const tabIndex = tabIndexParam || (onClick ? 0 : undefined);
    return (_jsxs("li", Object.assign({ sx: Object.assign(Object.assign({ depth, display: 'flex', mt: index === 0 && depth === 0 ? 0 : marginSizeMap[size], ml: 1 }, typographyStyles), sx), tabIndex: tabIndex }, rest, { children: [ordered ? (_jsxs(Label, Object.assign({ standalone: true, sx: { mr: 2 } }, { children: [index + 1, "."] }))) : (_jsx(Column, Object.assign({ sx: { height: lineHeight }, justify: "center" }, { children: _jsx(Icon, { icon: icon, color: iconColor, size: iconSize, onClick: onIconClick, "aria-label": iconLabel, sx: {
                        mr: 3,
                        alignItems: 'flex-start',
                    } }) }))), _jsx("div", Object.assign({ sx: {
                    cursor: onClick ? 'pointer' : 'default',
                } }, { children: children }))] })));
};
